import React, { forwardRef, useImperativeHandle, useEffect } from 'react';
import { useFormik, getIn } from 'formik';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { List, ListItem } from '@mui/material';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { TextFieldCustom } from '../helpers/formFields';
import { formatString } from './valueFunctions';
import { formatNumberThsndAndDec } from '../helpers/stringFormat';


const AddRevenueTypeForm = forwardRef(({ revenueTypes, queryResult, formActions, formResultActions, onSubmit, onSubmitResult }, ref) => {

    const initialValues = {
        id: null,
        revenue_type: '',
        multiple: '',
        wish: '',
    };

    const productTypeKeys = Object.keys(queryResult?.revenue_type || {});
    const firstKey = productTypeKeys.length > 0 ? productTypeKeys[0] : '';

    const queryValues = {
        id: queryResult?.id,
        revenue_type: firstKey || '',
        multiple: firstKey ? queryResult.revenue_type[firstKey]?.multiple : '',
        revenue_per_multiple: firstKey
            ? (queryResult.revenue_type[firstKey]?.revenue_per_multiple ?? { currency: '€', lower: '', higher: '' })
            : { currency: '€', lower: '', higher: '' },
        contribution_margin: firstKey
            ? (queryResult.revenue_type[firstKey]?.contribution_margin ?? { unit: '%', lower: '', higher: '' })
            : { unit: '%', lower: '', higher: '' },
    }

    const isNumeric = (value) => {
        return /^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(value);
    };

    const isPositive = (value) => {
        return parseFloat(value.toString().replace(/,/g, '')) >= 0;
    };

    const isLarger = (value1, value2) => {
        return parseFloat(value2.toString().replace(/,/g, '')) > parseFloat(value1.toString().replace(/,/g, ''));
    };

    const validate = (values) => {
        const errors = {};

        if (!values.revenue_type) {
            errors.revenue_type = 'Product Type is needed';
        }

        if (!values.multiple) {
            errors.multiple = 'Multiple is needed';
        }

        return errors
    }

    const validateResult = (values) => {
        const errors = {};

        if (!values.revenue_type) {
            errors.revenue_type = 'Product Type is needed';
        }

        if (Object.keys(queryResult).length > 0) {
            //     queryResult.cost_type[firstKey].multiple_type.forEach((type) => {
            //         // Check if values.cost_per_multiple exists and has a value for this type.
            if (!values.revenue_per_multiple || !values.contribution_margin) {
                return;
            }

            // Ensure we have an error object for this type.
            errors.revenue_per_multiple = errors.revenue_per_multiple || {};
            errors.contribution_margin = errors.contribution_margin || {};

            // Annual Revenue
            if (!isNumeric(values.revenue_per_multiple.lower)) {
                errors.revenue_per_multiple.lower = 'Must be numeric value';
            } else if (!isPositive(values.revenue_per_multiple.lower)) {
                errors.revenue_per_multiple.lower = 'Must be positive or zero';
            }

            if (!isNumeric(values.revenue_per_multiple.higher)) {
                errors.revenue_per_multiple.higher = 'Must be numeric value';
            } else if (!isPositive(values.revenue_per_multiple.higher)) {
                errors.revenue_per_multiple.higher = 'Must be positive';
            } else if (!isLarger(values.revenue_per_multiple.lower, values.revenue_per_multiple.higher)) {
                errors.revenue_per_multiple.higher = 'Must be higher than lower range';
            }

            // Contribution Margin
            if (!isNumeric(values.contribution_margin.lower)) {
                errors.contribution_margin.lower = 'Must be numeric value';
            } else if (!isPositive(values.contribution_margin.lower)) {
                errors.contribution_margin.lower = 'Must be positive or zero';
            }

            if (!isNumeric(values.contribution_margin.higher)) {
                errors.contribution_margin.higher = 'Must be numeric value';
            } else if (!isPositive(values.contribution_margin.higher)) {
                errors.contribution_margin.higher = 'Must be positive';
            } else if (!isLarger(values.contribution_margin.lower, values.contribution_margin.higher)) {
                errors.contribution_margin.higher = 'Must be higher than lower range';
            }


            if (Object.keys(errors.revenue_per_multiple).length === 0) {
                delete errors.revenue_per_multiple;
            }
            if (Object.keys(errors.contribution_margin).length === 0) {
                delete errors.contribution_margin;
            }
        }


        return errors
    }

    const formik = useFormik({
        initialValues: initialValues,
        validate: validate,
        onSubmit: onSubmit,
    });

    const formikResult = useFormik({
        initialValues: queryValues,
        validate: validateResult,
        onSubmit: onSubmitResult,
    });

    useImperativeHandle(ref, () => ({
        resetForm() {
            formik.handleReset()
        },
    }));

    useEffect(() => {
        if (Object.keys(queryResult).length > 0) {
            let revenue_per_multiple = { 'currency': '€', 'lower': '', 'higher': '' }
            if (firstKey && queryResult.revenue_type[firstKey]?.revenue_per_multiple) {
                revenue_per_multiple = { ...queryResult.revenue_type[firstKey].revenue_per_multiple }

                revenue_per_multiple = {
                    ...revenue_per_multiple,
                    currency: revenue_per_multiple.currency,
                    lower: formatNumberThsndAndDec(revenue_per_multiple.lower || ''),
                    higher: formatNumberThsndAndDec(revenue_per_multiple.higher || ''),
                }

            }


            formikResult.setFieldValue("id", queryResult?.id || 0)
            formikResult.setFieldValue("revenue_type", firstKey || '')
            formikResult.setFieldValue("multiple", firstKey ? queryResult.revenue_type[firstKey]?.multiple : '')
            formikResult.setFieldValue("revenue_per_multiple", revenue_per_multiple)
            formikResult.setFieldValue("contribution_margin", firstKey ? queryResult.revenue_type[firstKey]?.contribution_margin : { 'unit': '%', 'lower': '', 'higher': '' })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryResult])

    if (Object.keys(queryResult).length > 0 && formikResult.values.revenue_type) {
        return (
            <Box component="form" onSubmit={formikResult.handleSubmit}>
                <Grid container columnGap={1}>
                    <Grid item xs={8}>
                        <Grid container rowGap={2} columnGap={1}>
                            <Grid item xs={6}>
                                <Typography variant="caption">
                                    Name Revenue Type
                                </Typography>
                                <TextFieldCustom
                                    name="revenue_type"
                                    value={formikResult.values.revenue_type}
                                    onChange={formikResult.handleChange}
                                    onBlur={formikResult.handleBlur}
                                    error={(getIn(formikResult.errors, "revenue_type") && getIn(formikResult.touched, "revenue_type")) && Boolean(getIn(formikResult.errors, "revenue_type"))}
                                    helperText={getIn(formikResult.touched, "revenue_type") && getIn(formikResult.errors, "revenue_type")}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={4} />
                            <Grid item xs={6}>
                                <Typography variant="caption">
                                    Multiple
                                </Typography>
                                <TextFieldCustom
                                    name="multiple"
                                    value={formikResult.values.multiple}
                                    onChange={formikResult.handleChange}
                                    onBlur={formikResult.handleBlur}
                                    error={(getIn(formikResult.errors, "multiple") && getIn(formikResult.touched, "multiple")) && Boolean(getIn(formikResult.errors, "multiple"))}
                                    helperText={getIn(formikResult.touched, "multiple") && getIn(formikResult.errors, "multiple")}
                                    fullWidth
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={4} />
                            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                    Revenue per Multiple
                                </Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography variant="caption">
                                    Lower Range
                                </Typography>
                                <TextFieldCustom
                                    name={'revenue_per_multiple.lower'}
                                    startAdornment={formikResult.values.revenue_per_multiple.currency}
                                    value={formikResult.values.revenue_per_multiple.lower}
                                    onChange={(event) => {
                                        const { value } = event.target
                                        const formattedValue = formatNumberThsndAndDec(value)
                                        formikResult.setFieldValue('revenue_per_multiple.lower', formattedValue || '')
                                    }}
                                    onBlur={formikResult.handleBlur}
                                    error={(getIn(formikResult.errors, 'revenue_per_multiple.lower') && getIn(formikResult.touched, "revenue_per_multiple.lower")) && Boolean(getIn(formikResult.errors, 'revenue_per_multiple.lower'))}
                                    helperText={getIn(formikResult.touched, 'revenue_per_multiple.lower') && getIn(formikResult.errors, 'revenue_per_multiple.lower')}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Typography variant="caption">
                                    Higher Range
                                </Typography>
                                <TextFieldCustom
                                    name={'revenue_per_multiple.higher'}
                                    startAdornment={formikResult.values.revenue_per_multiple.currency}
                                    value={formikResult.values.revenue_per_multiple.higher}
                                    onChange={(event) => {
                                        const { value } = event.target
                                        const formattedValue = formatNumberThsndAndDec(value)
                                        formikResult.setFieldValue('revenue_per_multiple.higher', formattedValue || '')
                                    }}
                                    onBlur={formikResult.handleBlur}
                                    error={(getIn(formikResult.errors, 'revenue_per_multiple.higher') && getIn(formikResult.touched, "revenue_per_multiple.higher")) && Boolean(getIn(formikResult.errors, 'revenue_per_multiple.higher'))}
                                    helperText={getIn(formikResult.touched, 'revenue_per_multiple.higher') && getIn(formikResult.errors, 'revenue_per_multiple.higher')}
                                />
                            </Grid>

                            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                    Contribution Margin
                                </Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography variant="caption">
                                    Lower Range
                                </Typography>
                                <TextFieldCustom
                                    name={'contribution_margin.lower'}
                                    endAdornment={
                                        <Typography variant="caption">
                                            {formikResult.values.contribution_margin.unit}
                                        </Typography>
                                    }
                                    value={formikResult.values.contribution_margin.lower}
                                    onChange={(event) => {
                                        const { value } = event.target
                                        const formattedValue = formatNumberThsndAndDec(value)
                                        formikResult.setFieldValue('contribution_margin.lower', formattedValue || '')
                                    }}
                                    onBlur={formikResult.handleBlur}
                                    error={(getIn(formikResult.errors, 'contribution_margin.lower') && getIn(formikResult.touched, "contribution_margin.lower")) && Boolean(getIn(formikResult.errors, 'contribution_margin.lower'))}
                                    helperText={getIn(formikResult.touched, 'contribution_margin.lower') && getIn(formikResult.errors, 'contribution_margin.lower')}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Typography variant="caption">
                                    Higher Range
                                </Typography>
                                <TextFieldCustom
                                    name={'contribution_margin.higher'}
                                    endAdornment={
                                        <Typography variant="caption">
                                            {formikResult.values.contribution_margin.unit}
                                        </Typography>
                                    }
                                    value={formikResult.values.contribution_margin.higher}
                                    onChange={(event) => {
                                        const { value } = event.target
                                        const formattedValue = formatNumberThsndAndDec(value)
                                        formikResult.setFieldValue('contribution_margin.higher', formattedValue || '')
                                    }}
                                    onBlur={formikResult.handleBlur}
                                    error={(getIn(formikResult.errors, 'contribution_margin.higher') && getIn(formikResult.touched, "contribution_margin.higher")) && Boolean(getIn(formikResult.errors, 'contribution_margin.higher'))}
                                    helperText={getIn(formikResult.touched, 'contribution_margin.higher') && getIn(formikResult.errors, 'contribution_margin.higher')}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                {formResultActions()}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        )
    }

    return (
        <Box component="form" onSubmit={formik.handleSubmit}>
            <Grid container columnGap={1}>
                <Grid item xs={8}>
                    <Grid container rowGap={2} columnGap={1}>
                        <Grid item xs={6}>
                            <Typography variant="caption">
                                Input Revenue Type to request
                            </Typography>
                            <TextFieldCustom
                                name="revenue_type"
                                value={formik.values.revenue_type}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={(getIn(formik.errors, "revenue_type") && getIn(formik.touched, "revenue_type")) && Boolean(getIn(formik.errors, "revenue_type"))}
                                helperText={getIn(formik.touched, "revenue_type") && getIn(formik.errors, "revenue_type")}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={4} />
                        <Grid item xs={6}>
                            <Typography variant="caption">
                                Input Multiple
                            </Typography>
                            <TextFieldCustom
                                name="multiple"
                                value={formik.values.multiple}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={(getIn(formik.errors, "multiple") && getIn(formik.touched, "multiple")) && Boolean(getIn(formik.errors, "multiple"))}
                                helperText={getIn(formik.touched, "multiple") && getIn(formik.errors, "multiple")}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={4} />
                        <Grid item xs={12}>
                            <Typography variant="caption">
                                Specify a wish if you would like to put your request in some context
                            </Typography>
                            <TextFieldCustom
                                name="wish"
                                value={formik.values.wish}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "wish") && Boolean(getIn(formik.errors, "wish"))}
                                helperText={getIn(formik.touched, "wish") && getIn(formik.errors, "wish")}
                                fullWidth
                            />
                            <Typography variant="caption">
                                E.g. "Large company of type X producing products of type Y"
                            </Typography>
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 3, mb: 4 }}>
                        {formActions()}
                    </Box>
                </Grid>

                <Grid item xs={3}>
                    <Paper elevation={3} sx={{ p: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Existing Revenue Types:
                        </Typography>
                        <List sx={{ padding: 0, listStyleType: 'disc', listStylePosition: 'inside' }}>
                            {Object.keys(revenueTypes).map((revenue_type, index) => (
                                <ListItem
                                    key={index}
                                    sx={{
                                        display: 'list-item',
                                        paddingY: 0.5,
                                        paddingLeft: 2,
                                    }}
                                >
                                    <Typography variant="caption" sx={{ fontWeight: 'normal' }}>
                                        {formatString(revenue_type)}
                                    </Typography>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Grid>

            </Grid>
        </Box>
    )
})

export default AddRevenueTypeForm
